/**
 * Demo dataset: a synthetic project so the field app and the dashboard can be tried
 * before a backend exists (and so a store reviewer can walk the survey flow).
 *
 * Nothing here is a real tree, site or team. Every id starts with DEMO_ID_PREFIX so
 * the apps can recognise and wipe demo rows; tags are TEST-A-nnn / TEST-B-nnn, the
 * project code is TEST, and the trees are laid out in a grid around whatever centre
 * the caller passes (the phone passes its own GPS fix, so the near-me list and the
 * map work wherever the demo is opened). The species names are three from
 * supabase/seed.species.reference.csv.
 *
 * Two surveys exist so growth, flags and ghosted "last values" have something to
 * show: a baseline about eleven months ago and a follow-up about five months ago.
 * The view rows (tree_latest, survey_summary, zone_survival_by_survey, tree_flags)
 * are computed with the same math as the harness, so the dashboard reads the demo
 * exactly as it would read the database.
 */
import { PROTOCOL } from './constants';
import type { Condition } from './enums';
import { possibleMisTag } from './geo';
import { countConditions, cumulativeSurvivalPct, diameterFromGirthCm, statusFromHistory, stressedFlag, survivalRate } from './math';
import type {
  GirthRow,
  InterventionRow,
  MeasurementRow,
  ProjectRow,
  SpeciesRow,
  SurveyRow,
  SurveySummaryRow,
  TreeFlagsRow,
  TreeLatestRow,
  TreeRow,
  ZoneRow,
  ZoneSurvivalRow,
} from './types';

export const DEMO_ID_PREFIX = 'dddddddd-0000-4000-8000-';
export const demoId = (n: number): string => `${DEMO_ID_PREFIX}${String(n).padStart(12, '0')}`;
export const isDemoId = (id: string | null | undefined): boolean => typeof id === 'string' && id.startsWith(DEMO_ID_PREFIX);

export const DEMO_PROJECT_ID = demoId(1);
export const DEMO_TEAM_ID = demoId(2);
export const DEMO_PROJECT_CODE = 'TEST';
export const DEMO_TEAM_NAME = 'Equipo Demo';
export const DEMO_ZONE_A = demoId(10);
export const DEMO_ZONE_B = demoId(11);

export interface DemoDataset {
  project: ProjectRow;
  zones: ZoneRow[];
  species: SpeciesRow[];
  trees: TreeRow[];
  surveys: SurveyRow[];
  measurements: MeasurementRow[];
  girths: GirthRow[];
  interventions: InterventionRow[];
  /** The view rows, computed with the protocol math. */
  latest: TreeLatestRow[];
  summaries: SurveySummaryRow[];
  zoneSurvival: ZoneSurvivalRow[];
  flags: TreeFlagsRow[];
}

const M_PER_DEG_LAT = 111_195;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (today: Date, n: number) => {
  const d = new Date(today);
  d.setUTCDate(d.getUTCDate() - n);
  return d;
};

export function buildDemoDataset(opts: { centerLat: number; centerLng: number; today?: Date }): DemoDataset {
  const today = opts.today ?? new Date();
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((opts.centerLat * Math.PI) / 180);
  const at = (eastM: number, northM: number) => ({ lat: opts.centerLat + northM / M_PER_DEG_LAT, lng: opts.centerLng + eastM / mPerDegLng });
  const rect = (e0: number, n0: number, e1: number, n1: number) => {
    const a = at(e0, n0);
    const b = at(e1, n1);
    return { type: 'Polygon' as const, coordinates: [[[a.lng, a.lat], [b.lng, a.lat], [b.lng, b.lat], [a.lng, b.lat], [a.lng, a.lat]]] };
  };

  const baselineDate = daysAgo(today, 330);
  const followupDate = daysAgo(today, 150);
  const plantedOn = isoDay(daysAgo(today, 340));
  const stamp = (d: Date, h = 15) => `${isoDay(d)}T${String(h).padStart(2, '0')}:00:00.000Z`;

  const species: SpeciesRow[] = [
    { id: demoId(20), name_es: 'Guanacaste', name_en: 'Guanacaste', scientific: 'Enterolobium cyclocarpum', type: 'hardwood', native: true, notes: null, created_at: stamp(baselineDate), updated_at: stamp(baselineDate) },
    { id: demoId(21), name_es: 'Cedro real', name_en: 'Spanish cedar', scientific: 'Cedrela odorata', type: 'hardwood', native: true, notes: null, created_at: stamp(baselineDate), updated_at: stamp(baselineDate) },
    { id: demoId(22), name_es: 'Nancite', name_en: 'Nance', scientific: 'Byrsonima crassifolia', type: 'fruit', native: true, notes: null, created_at: stamp(baselineDate), updated_at: stamp(baselineDate) },
  ];

  // Zone A: 3 columns x 4 rows, 4 m apart, starting 8 m east / 8 m north of the centre.
  // Zone B: 3 x 2, 30 m east.
  const zones: ZoneRow[] = [
    { id: DEMO_ZONE_A, project_id: DEMO_PROJECT_ID, label: 'A', geom_geojson: rect(5, 5, 21, 25), centroid_lat: at(13, 15).lat, centroid_lng: at(13, 15).lng, grid_rows: 4, grid_cols: 3, created_at: stamp(baselineDate), updated_at: stamp(baselineDate) },
    { id: DEMO_ZONE_B, project_id: DEMO_PROJECT_ID, label: 'B', geom_geojson: rect(27, 5, 43, 17), centroid_lat: at(35, 11).lat, centroid_lng: at(35, 11).lng, grid_rows: 2, grid_cols: 3, created_at: stamp(baselineDate), updated_at: stamp(baselineDate) },
  ];

  const project: ProjectRow = {
    id: DEMO_PROJECT_ID,
    code: DEMO_PROJECT_CODE,
    name: 'Demo · datos sintéticos / synthetic data',
    site: 'Demo',
    boundary_geojson: rect(0, 0, 48, 30),
    planted_on: plantedOn,
    lead_user: null,
    basemap_path: null,
    created_at: stamp(baselineDate),
    updated_at: stamp(baselineDate),
  };

  const trees: TreeRow[] = [];
  let n = 100;
  const addGrid = (zone: ZoneRow, zoneLetter: string, cols: number, rows: number, east0: number, north0: number, count: number) => {
    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const p = at(east0 + col * 4, north0 + row * 4);
      n += 1;
      trees.push({
        id: demoId(n),
        client_id: demoId(n),
        project_id: DEMO_PROJECT_ID,
        zone_id: zone.id,
        tag: `${DEMO_PROJECT_CODE}-${zoneLetter}-${String(i + 1).padStart(3, '0')}`,
        species_id: species[i % 3].id,
        lat: p.lat,
        lng: p.lng,
        gps_accuracy_m: 4 + (i % 4),
        grid_row: row + 1,
        grid_col: col + 1,
        planted_on: plantedOn,
        status: 'active',
        replaced_by: null,
        created_by: null,
        created_at: stamp(baselineDate, 9),
        updated_at: stamp(baselineDate, 9),
      });
    }
  };
  addGrid(zones[0], 'A', 3, 4, 8, 8, 12);
  addGrid(zones[1], 'B', 3, 2, 30, 8, 6);

  const surveys: SurveyRow[] = [
    {
      id: demoId(300),
      client_id: demoId(300),
      project_id: DEMO_PROJECT_ID,
      zone_id: null,
      date: isoDay(baselineDate),
      team_id: DEMO_TEAM_ID,
      team_name: DEMO_TEAM_NAME,
      members: 'AB, CD',
      weather: ['sunny', 'hot'],
      care_plan: 'Mulch y riego semanal en la zona A.',
      mulch_plan: 'Hojarasca del bosque.',
      pest_notes: 'Zompopos en el borde este.',
      irrigation_bottles: 12,
      notes: 'Línea base al sembrar.',
      created_by: null,
      device_time: stamp(baselineDate, 16),
      created_at: stamp(baselineDate, 16),
      updated_at: stamp(baselineDate, 16),
    },
    {
      id: demoId(301),
      client_id: demoId(301),
      project_id: DEMO_PROJECT_ID,
      zone_id: null,
      date: isoDay(followupDate),
      team_id: DEMO_TEAM_ID,
      team_name: DEMO_TEAM_NAME,
      members: 'AB, EF',
      weather: ['cloudy'],
      care_plan: 'Reponer mulch donde falta.',
      mulch_plan: null,
      pest_notes: null,
      irrigation_bottles: 8,
      notes: null,
      created_by: null,
      device_time: stamp(followupDate, 16),
      created_at: stamp(followupDate, 16),
      updated_at: stamp(followupDate, 16),
    },
  ];

  // Per-tree story (by tag). Default: healthy and growing.
  const story: Record<string, { c1: Condition; c2: Condition | null; chips2?: string[]; misTag?: boolean }> = {
    'TEST-A-002': { c1: 'H', c2: 'H' }, // grows past 150 cm -> DBH girth
    'TEST-A-004': { c1: 'S', c2: 'S', chips2: ['dry_leaves', 'no_mulch'] }, // stressed twice
    'TEST-A-005': { c1: 'H', c2: 'H', misTag: true }, // device fix 20 m off -> possible mis-tag
    'TEST-A-007': { c1: 'H', c2: 'D' },
    'TEST-A-010': { c1: 'H', c2: 'NF' },
    'TEST-B-003': { c1: 'NF', c2: 'NF' }, // not found twice -> dead
  };

  const measurements: MeasurementRow[] = [];
  const girths: GirthRow[] = [];
  let mid = 400;
  const measure = (survey: SurveyRow, tree: TreeRow, idx: number, condition: Condition, heightCm: number | null, girth10: number | null, chips: string[], misTag: boolean) => {
    mid += 1;
    const m: MeasurementRow = {
      id: demoId(mid),
      client_id: demoId(mid),
      tree_id: tree.id,
      survey_id: survey.id,
      condition,
      height_cm: condition === 'NF' || condition === 'D' ? null : heightCm,
      height_method: null,
      soil_moisture: condition === 'NF' ? null : idx % 3 === 0 ? 'dry' : 'moist',
      soil_temp: condition === 'NF' ? null : idx % 2 === 0 ? 'warm' : 'cool',
      chips,
      note: null,
      device_lat: misTag ? (tree.lat as number) + 20 / M_PER_DEG_LAT : tree.lat,
      device_lng: tree.lng,
      device_accuracy_m: 5,
      recorded_by: null,
      team_id: DEMO_TEAM_ID,
      device_time: stamp(new Date(survey.date), 10),
      recorded_at: stamp(new Date(survey.date), 10),
    };
    measurements.push(m);
    if (condition !== 'NF' && condition !== 'D' && girth10 != null) {
      girths.push({ measurement_id: m.id, height_cm: 10, girth_cm: girth10, diameter_cm: Math.round(diameterFromGirthCm(girth10) * 1000) / 1000 });
      if (heightCm != null && heightCm > PROTOCOL.DBH_TRIGGER_HEIGHT_CM) {
        const g130 = Math.round(girth10 * 0.45 * 10) / 10;
        girths.push({ measurement_id: m.id, height_cm: 130, girth_cm: g130, diameter_cm: Math.round(diameterFromGirthCm(g130) * 1000) / 1000 });
      }
    }
  };
  trees.forEach((tree, i) => {
    const s = story[tree.tag] ?? { c1: 'H' as Condition, c2: 'H' as Condition };
    const h1 = 40 + (i % 7) * 5; // 40–70 cm at planting
    const g1 = Math.round((2 + (i % 5) * 0.4) * 10) / 10; // 2.0–3.6 cm
    const growth = tree.tag === 'TEST-A-002' ? 3.8 : 1.4 + (i % 4) * 0.12; // A-002 passes 150 cm, so the DBH girth appears
    measure(surveys[0], tree, i, s.c1, s.c1 === 'NF' ? null : h1, s.c1 === 'NF' ? null : g1, s.c1 === 'S' ? ['dry_leaves'] : [], false);
    if (s.c2) measure(surveys[1], tree, i, s.c2, Math.round(h1 * growth), Math.round(g1 * growth * 10) / 10, s.chips2 ?? [], Boolean(s.misTag));
  });

  const interventions: InterventionRow[] = [
    { id: demoId(500), client_id: demoId(500), project_id: DEMO_PROJECT_ID, tree_id: null, zone_id: DEMO_ZONE_A, survey_id: surveys[1].id, type: 'watered', quantity: 120, unit: 'L', note: 'Riego de toda la zona A', done_by: null, team_id: DEMO_TEAM_ID, done_at: stamp(followupDate, 11), recorded_at: stamp(followupDate, 11) },
    { id: demoId(501), client_id: demoId(501), project_id: DEMO_PROJECT_ID, tree_id: trees[3].id, zone_id: null, survey_id: surveys[1].id, type: 'mulched', quantity: null, unit: null, note: null, done_by: null, team_id: DEMO_TEAM_ID, done_at: stamp(followupDate, 11), recorded_at: stamp(followupDate, 11) },
    { id: demoId(502), client_id: demoId(502), project_id: DEMO_PROJECT_ID, tree_id: trees[5].id, zone_id: null, survey_id: surveys[1].id, type: 'ant_control', quantity: null, unit: null, note: 'Zompopos', done_by: null, team_id: DEMO_TEAM_ID, done_at: stamp(followupDate, 12), recorded_at: stamp(followupDate, 12) },
  ];

  // ---- the view rows -------------------------------------------------------
  const bySurvey = new Map(surveys.map((s) => [s.id, s]));
  const girthsByM = new Map<string, GirthRow[]>();
  for (const g of girths) girthsByM.set(g.measurement_id, [...(girthsByM.get(g.measurement_id) ?? []), g]);
  const history = new Map<string, MeasurementRow[]>();
  for (const s of surveys) for (const m of measurements.filter((m) => m.survey_id === s.id)) history.set(m.tree_id, [...(history.get(m.tree_id) ?? []), m]);

  const latest: TreeLatestRow[] = trees.map((t) => {
    const h = history.get(t.id) ?? [];
    const m = h[h.length - 1] ?? null;
    const gs = m ? (girthsByM.get(m.id) ?? []) : [];
    const gb = gs.find((g) => g.height_cm === 10) ?? null;
    const gd = gs.find((g) => g.height_cm === 130) ?? null;
    return {
      tree_id: t.id,
      project_id: t.project_id,
      zone_id: t.zone_id,
      tag: t.tag,
      species_id: t.species_id,
      status: statusFromHistory(h.map((x) => x.condition)),
      measurement_id: m?.id ?? null,
      survey_date: m ? (bySurvey.get(m.survey_id)?.date ?? null) : null,
      condition: m?.condition ?? null,
      height_cm: m?.height_cm ?? null,
      girth_basal_cm: gb?.girth_cm ?? null,
      diameter_basal_cm: gb?.diameter_cm ?? null,
      girth_dbh_cm: gd?.girth_cm ?? null,
      diameter_dbh_cm: gd?.diameter_cm ?? null,
      recorded_at: m?.recorded_at ?? null,
    };
  });
  for (const t of trees) t.status = latest.find((l) => l.tree_id === t.id)?.status ?? 'active';

  const summaries: SurveySummaryRow[] = surveys.map((s) => {
    const r = survivalRate(countConditions(measurements.filter((m) => m.survey_id === s.id).map((m) => m.condition)));
    return { survey_id: s.id, project_id: s.project_id, zone_id: s.zone_id, date: s.date, team_name: s.team_name, found: r.found, alive: r.alive, not_found: r.notFound, survival_rate_pct: r.survivalRatePct };
  });

  const zoneSurvival: ZoneSurvivalRow[] = [];
  for (const z of zones) {
    const planted = trees.filter((t) => t.zone_id === z.id).length;
    for (const s of surveys) {
      const conds = measurements.filter((m) => m.survey_id === s.id && trees.find((t) => t.id === m.tree_id)?.zone_id === z.id).map((m) => m.condition);
      const r = survivalRate(countConditions(conds));
      zoneSurvival.push({
        survey_id: s.id,
        project_id: s.project_id,
        zone_id: z.id,
        zone_label: z.label,
        date: s.date,
        team_name: s.team_name,
        found: r.found,
        alive: r.alive,
        not_found: r.notFound,
        survival_rate_pct: r.survivalRatePct,
        planted,
        cumulative_survival_pct: cumulativeSurvivalPct(planted, r.alive),
      });
    }
  }

  const flags: TreeFlagsRow[] = trees.map((t) => {
    const h = history.get(t.id) ?? [];
    const conds = h.map((m) => m.condition);
    return {
      tree_id: t.id,
      stressed_twice: stressedFlag(conds),
      not_found_last: conds[conds.length - 1] === 'NF',
      not_found_twice: conds.length >= 2 && conds.slice(-2).every((c) => c === 'NF'),
      possible_mis_tag: h.some((m) => m.device_lat != null && m.device_lng != null && t.lat != null && t.lng != null && possibleMisTag({ lat: m.device_lat, lng: m.device_lng }, { lat: t.lat, lng: t.lng as number })),
    };
  });

  return { project, zones, species, trees, surveys, measurements, girths, interventions, latest, summaries, zoneSurvival, flags };
}
